import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import KpiCard from "../components/KpiCard";
import TankGauge from "../components/TankGauge";
import AnomalyDigest from "../components/AnomalyDigest";
import { ghs, money, litres, num, formatDate } from "../lib/format";
import { useCountry, countryInfo } from "../context/CountryContext";
import { GasPump, CurrencyCircleDollar, Truck, ChartLineUp, WarningOctagon, Flame, Stack, ArrowRight, Drop } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isTG } = useCountry();
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/dashboard").then(({ data }) => {
      if (alive) { setData(data); setLoading(false); }
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading || !data) {
    return (
      <div className="p-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Loading…</div>
        <div className="indus-bar w-1/3" />
      </div>
    );
  }

  if (isTG) return <TogoDashboard />;

  const { stock, today, months, recent } = data;

  return (
    <div data-testid="dashboard-page">
      <PageHeader subtitle="Operations console" title="Yard Dashboard" />

      <PendingActivitiesBanner />

      {stock.is_low && (
        <div className="bg-ps-red text-white px-8 py-3 flex items-center gap-3" data-testid="low-stock-banner">
          <WarningOctagon size={22} weight="fill" />
          <div className="font-semibold uppercase tracking-wider text-sm">
            Low fuel alert · Current reservoir at {num(stock.current_litres)} L (threshold {num(stock.low_threshold)} L). Schedule a delivery.
          </div>
        </div>
      )}

      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard testid="kpi-current-stock" label="Reservoir" value={num(stock.current_litres)} unit="litres on hand" accent="blue" icon={GasPump} />
        <KpiCard testid="kpi-today-dispensed" label="Today dispensed" value={num(today.fuel_litres)} unit={`${today.dispense_count} trips · ${ghs(today.fuel_cost_ghs)}`} accent="cyan" icon={GasPump} />
        <KpiCard testid="kpi-today-money" label="Money sent today" value={ghs(today.money_sent_ghs)} unit={`${today.transfer_count} transfers`} accent="lime" icon={CurrencyCircleDollar} />
        {data.anomalies_30d && data.anomalies_30d.count > 0 ? (
          <button
            type="button"
            onClick={() => nav("/reports?tab=anomalies")}
            data-testid="kpi-anomalies"
            className="text-left"
          >
            <KpiCard
              testid="kpi-anomalies-card"
              label="Fuel anomalies (30d)"
              value={num(data.anomalies_30d.count)}
              unit={`+${num(data.anomalies_30d.extra_litres)} L · ${ghs(data.anomalies_30d.extra_cost_ghs)} above baseline`}
              accent="red"
              icon={Flame}
            />
          </button>
        ) : (
          <KpiCard testid="kpi-active-vehicles" label="Active vehicles" value={num(data.active_vehicles_30d)} unit={`last 30 days · ${data.total_trucks} registered`} icon={Truck} />
        )}
      </div>

      <div className="px-8 pb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tank */}
        <div className="lg:col-span-1">
          <TankGauge litres={stock.current_litres} capacity={stock.capacity} low={stock.is_low} />
        </div>

        {/* Chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 card-lift" data-testid="monthly-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">6-month trend</div>
              <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">Fuel volume vs. cash flow</h3>
            </div>
            <div className="w-9 h-9 grid place-items-center rounded-md bg-sky-50 text-ps-blue">
              <ChartLineUp size={18} weight="duotone" />
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%" minHeight={240} minWidth={200} debounce={50}>
              <BarChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={24}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "Manrope", fill: "#475569" }} stroke="#CBD5E1" tickLine={false} axisLine={false} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#006f9e" }} stroke="#CBD5E1" tickLine={false} axisLine={false} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#85a309" }} stroke="#CBD5E1" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ border: "1px solid #CBD5E1", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} cursor={{ fill: "rgba(0,111,158,0.04)" }} />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.2em", fontFamily: "Manrope", fontWeight: 600 }} />
                <Bar yAxisId="l" dataKey="litres" name="Fuel (L)" fill="#006f9e" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="r" dataKey="money_sent_ghs" name="Money Sent (GHS)" fill="#afca0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent activity */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="recent-activity-card">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Live feed</div>
              <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">Recent activity</h3>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {recent.length === 0 && (
              <div className="px-6 py-8 text-slate-500 text-sm">No activity yet.</div>
            )}
            {recent.map((r) => (
              <div key={`${r.type}-${r.id}`} className="px-6 py-3 grid grid-cols-12 gap-4 items-center hover:bg-slate-50 transition-colors">
                <div className="col-span-2 text-xs font-mono-data text-slate-500">{formatDate(r.date)}</div>
                <div className="col-span-1">
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${r.type === "dispense" ? "bg-sky-50 text-ps-blue" : "bg-lime-50 text-lime-700"}`}>
                    {r.type === "dispense" ? "Fuel" : "Cash"}
                  </span>
                </div>
                <div className="col-span-6">
                  <div className="font-semibold text-ps-deep">{r.title}</div>
                  <div className="text-xs text-slate-500">{r.subtitle}</div>
                </div>
                <div className="col-span-3 text-right font-mono-data tabular text-ps-deep font-semibold">
                  {ghs(r.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Anomaly digest */}
        <div className="lg:col-span-1">
          <AnomalyDigest />
        </div>

        {/* Yard equipment (stations — washing station, Kalmar handler…) */}
        <div className="lg:col-span-3">
          <StationsUsageCard />
        </div>
      </div>
    </div>
  );
}

function StationsUsageCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/stations/usage").then(({ data }) => {
      if (alive) setRows(data.stations || []);
    }).catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, []);
  if (!rows) return null;
  if (rows.length === 0) return null;
  const monthLabel = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 card-lift" data-testid="stations-usage-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-lime-700 font-semibold">Yard equipment · {monthLabel}</div>
          <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">Station fuel usage</h3>
          <p className="text-xs text-slate-500 mt-1">Fuel drawn by non-truck yard assets — tracked separately so the fleet stats stay clean.</p>
        </div>
        <div className="w-9 h-9 grid place-items-center rounded-md bg-lime-50 text-lime-700">
          <Drop size={18} weight="duotone" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((s) => {
          const ratio = s.litres_per_unit;
          const singular = singularUnit(s.unit_label);
          return (
            <div key={s.vehicle_number} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors" data-testid={`station-tile-${s.vehicle_number}`}>
              <div className="flex items-baseline justify-between">
                <div className="font-mono-data text-sm font-semibold text-ps-deep tracking-wide">{s.vehicle_number}</div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.dispenses} dispense{s.dispenses === 1 ? "" : "s"}</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Litres</div>
                  <div className="font-mono-data tabular text-lg text-ps-blue font-bold">{num(s.litres)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 capitalize">{s.unit_label}</div>
                  <div className="font-mono-data tabular text-lg text-ps-deep font-bold">{num(s.unit_count)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">L / {singular}</div>
                  <div className="font-mono-data tabular text-lg text-lime-700 font-bold">{ratio != null ? ratio : "—"}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Convert an English plural unit label to its singular form for "L / wash", "L / move", …
// Uses a small explicit map for irregular plurals ("washes" -> "wash"), falls back to naïve
// suffix stripping (-s / -es) for anything else. Safe for uppercased and lowercased inputs.
function singularUnit(label) {
  if (!label) return "unit";
  const lower = String(label).toLowerCase();
  const irregular = { washes: "wash", boxes: "box", batches: "batch", cleans: "clean", flushes: "flush", passes: "pass" };
  if (irregular[lower]) return irregular[lower];
  if (lower.endsWith("ies") && lower.length > 3) return lower.slice(0, -3) + "y";
  if (lower.endsWith("s") && lower.length > 1) return lower.slice(0, -1);
  return lower;
}

// --- Togo dashboard: no fuel station — Activities + Money on Road (XOF) + CMRs ---
function TogoDashboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [trips, setTrips] = useState([]);
  const [activities, setActivities] = useState([]);
  const [productivity, setProductivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const { COUNTRIES } = useCountry();

  useEffect(() => {
    let alive = true;
    const month = new Date().toISOString().slice(0, 7); // YYYY-MM
    Promise.all([
      api.get("/transfers", { params: { country: "TG" } }),
      api.get("/trips", { params: { country: "TG", limit: 6 } }),
      api.get("/activities", { params: { country: "TG", limit: 100 } }),
      api.get("/activities/productivity", { params: { country: "TG", month } }).catch(() => ({ data: null })),
    ]).then(([t, p, a, prod]) => {
      if (!alive) return;
      setRows(t.data);
      setTrips(p.data);
      setActivities(a.data);
      setProductivity(prod.data);
      setLoading(false);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const totals = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let sent = 0, settled = 0, todaySent = 0, todayCount = 0;
    for (const r of rows) {
      sent += r.amount;
      if (r.status === "Settled") settled += r.amount;
      if (new Date(r.date) >= today) { todaySent += r.amount; todayCount += 1; }
    }
    return { sent, settled, outstanding: sent - settled, todaySent, todayCount };
  }, [rows]);

  // Activity rollup for the KPI card (this month)
  const actStats = useMemo(() => {
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    let count = 0, fuelL = 0;
    for (const a of activities) {
      if (new Date(a.date) >= monthStart) {
        count += 1;
        fuelL += a.fuel_issued_l || a.fuel_approved_l || a.fuel_requested_l || 0;
      }
    }
    return { count, fuelL };
  }, [activities]);

  return (
    <div data-testid="togo-dashboard">
      <PageHeader subtitle={`${COUNTRIES.TG.flag} Togo · Driver cash desk`} title="Togo Dashboard" />
      <PendingActivitiesBanner />
      <div className="px-8 pt-3 pb-1">
        <div className="text-xs text-slate-500" data-testid="togo-no-fuel-note">
          Togo has no yard fuel station — only activities, money on road (XOF) and trip waybills are tracked here.
        </div>
      </div>
      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard testid="tg-kpi-activities" label="Activities (this month)" value={num(actStats.count)} unit={`${litres(actStats.fuelL)} fuel issued`} accent="blue" icon={Stack} />
        <KpiCard testid="tg-kpi-total-sent" label="Total sent (all time)" value={money(totals.sent, "XOF")} unit={`${rows.length} transfers`} accent="lime" icon={CurrencyCircleDollar} />
        <KpiCard testid="tg-kpi-settled" label="Settled" value={money(totals.settled, "XOF")} unit="acknowledged by drivers" accent="lime" icon={CurrencyCircleDollar} />
        <KpiCard testid="tg-kpi-outstanding" label="Outstanding" value={money(totals.outstanding, "XOF")} unit="awaiting settlement" accent="red" icon={WarningOctagon} />
      </div>

      {/* Productivity rollup if there are activities */}
      {productivity && productivity.total_trips > 0 && (
        <div className="px-8 pb-4" data-testid="tg-productivity">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Monthly productivity</div>
                <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">
                  {num(productivity.total_trips)} trips · {litres(productivity.total_fuel_l)}
                </h3>
              </div>
              <button onClick={() => nav("/activities")} className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep">View activities →</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Trips per truck</div>
                {(productivity.by_truck || []).slice(0, 6).map((t) => {
                  const pct = productivity.by_truck[0] ? Math.round((t.trips / productivity.by_truck[0].trips) * 100) : 0;
                  return (
                    <div key={t.vehicle_number} className="flex items-center gap-3 py-1" data-testid={`tg-prod-truck-${t.vehicle_number}`}>
                      <div className="w-20 font-mono-data text-sm font-semibold text-ps-deep shrink-0">{t.vehicle_number}</div>
                      <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden">
                        <div className="bg-ps-blue h-full" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-12 text-right font-mono-data text-sm">{t.trips}</div>
                      <div className="w-16 text-right text-xs text-slate-500">{Math.round(t.fuel_l)} L</div>
                    </div>
                  );
                })}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Trips per client</div>
                {Object.entries(
                  activities
                    .filter((a) => new Date(a.date) >= new Date(new Date().getFullYear(), new Date().getMonth(), 1))
                    .reduce((acc, a) => { acc[a.client || "—"] = (acc[a.client || "—"] || 0) + 1; return acc; }, {})
                )
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([client, c]) => (
                    <div key={client} className="flex items-center gap-3 py-1" data-testid={`tg-prod-client-${client}`}>
                      <div className="flex-1 text-sm font-semibold text-ps-deep truncate">{client}</div>
                      <div className="w-12 text-right font-mono-data text-sm">{c}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-8 pb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent activities (the big new addition) */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="tg-recent-activities">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Operations · Activités</div>
              <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">Recent activities</h3>
            </div>
            <button onClick={() => nav("/activities")} className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep flex items-center gap-1">View all <ArrowRight size={12} weight="bold" /></button>
          </div>
          <div className="divide-y divide-slate-100">
            {loading && <div className="px-6 py-4"><div className="indus-bar w-1/3" /></div>}
            {!loading && activities.length === 0 && <div className="px-6 py-8 text-slate-500 text-sm">No activities yet. Use the <span className="font-semibold text-ps-blue">Import Excel</span> button on the Activities page to load Louisa&apos;s monthly file.</div>}
            {activities.slice(0, 10).map((a) => (
              <div key={a.id} className="px-6 py-3 grid grid-cols-12 gap-4 items-center hover:bg-slate-50">
                <div className="col-span-2 text-xs font-mono-data text-slate-500">{formatDate(a.date)}</div>
                <div className="col-span-2 font-mono-data text-sm font-semibold text-ps-blue">{a.vehicle_number}</div>
                <div className="col-span-3">
                  <div className="font-semibold text-ps-deep truncate">{a.client}</div>
                  <div className="text-xs text-slate-500 truncate">{a.activity_detail || a.activity_type}</div>
                </div>
                <div className="col-span-2 text-[10px] font-bold uppercase tracking-wider text-slate-600">{a.status?.replace("_", " ")}</div>
                <div className="col-span-3 text-right font-mono-data tabular text-ps-deep font-semibold">{litres(a.fuel_issued_l || a.fuel_approved_l || a.fuel_requested_l || 0)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar: recent transfers + recent CMRs */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="tg-recent-transfers">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Cash desk</div>
                <h3 className="font-display font-extrabold text-base tracking-tight text-ps-deep">Recent transfers</h3>
              </div>
              <button onClick={() => nav("/money")} className="text-[10px] uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep">All →</button>
            </div>
            <div className="divide-y divide-slate-100">
              {loading && <div className="px-5 py-3"><div className="indus-bar w-1/3" /></div>}
              {!loading && rows.length === 0 && <div className="px-5 py-6 text-slate-500 text-sm italic">No transfers yet.</div>}
              {rows.slice(0, 5).map((r) => (
                <div key={r.id} className="px-5 py-2 hover:bg-slate-50">
                  <div className="flex items-baseline justify-between">
                    <div className="font-semibold text-sm text-ps-deep truncate">{r.driver_name}</div>
                    <div className="font-mono-data tabular text-ps-deep text-sm font-semibold">{money(r.amount, "XOF")}</div>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{formatDate(r.date)} · {r.purpose}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="tg-recent-trips">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Logistique</div>
                <h3 className="font-display font-extrabold text-base tracking-tight text-ps-deep">Recent CMRs</h3>
              </div>
              <button onClick={() => nav("/trips")} className="text-[10px] uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep">All →</button>
            </div>
            <div className="divide-y divide-slate-100">
              {loading && <div className="px-5 py-3"><div className="indus-bar w-1/3" /></div>}
              {!loading && trips.length === 0 && <div className="px-5 py-6 text-slate-500 text-sm italic">No CMRs yet.</div>}
              {trips.slice(0, 4).map((t) => (
                <button
                  key={t.id}
                  onClick={() => nav(`/trips/${t.id}`)}
                  className="w-full text-left px-5 py-2 hover:bg-slate-50 block"
                >
                  <div className="font-mono-data text-ps-blue text-xs font-semibold">{t.cmr_number}</div>
                  <div className="text-sm font-semibold text-ps-deep truncate">{t.consignee?.name || "—"}</div>
                  <div className="text-xs text-slate-500 truncate">{t.origin} → {t.destination}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Pending activity (fuel request) banner -----
function PendingActivitiesBanner() {
  const [pending, setPending] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    let alive = true;
    const fetchPending = () =>
      api.get("/activities/pending-approval")
        .then(({ data }) => alive && setPending(data || []))
        .catch(() => {});
    fetchPending();
    // light polling so approvers see new requests within a minute
    const id = setInterval(fetchPending, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!pending.length) return null;
  const totalL = pending.reduce((s, p) => s + (p.fuel_requested_l || 0), 0);
  return (
    <div className="bg-amber-50 border-y border-amber-200 px-8 py-3" data-testid="pending-activities-banner">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
          <GasPump size={18} weight="bold" />
          <span className="uppercase tracking-wider">{pending.length} fuel request{pending.length === 1 ? "" : "s"} awaiting approval</span>
          <span className="text-amber-700 font-mono-data normal-case tracking-normal">· total {num(totalL)} L</span>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-amber-700 ml-2">
          {pending.slice(0, 3).map((p) => (
            <span key={p.id} className="bg-white border border-amber-200 rounded px-2 py-0.5 font-mono-data">{p.vehicle_number} · {num(p.fuel_requested_l)} L · {p.client}</span>
          ))}
          {pending.length > 3 && <span className="text-amber-600">+{pending.length - 3} more</span>}
        </div>
        <button
          onClick={() => nav("/activities?status=requested")}
          data-testid="pending-banner-review"
          className="ml-auto bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-md font-semibold text-sm flex items-center gap-1"
        >
          Review now →
        </button>
      </div>
    </div>
  );
}

